import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Plus, Trash2, Loader2 } from "lucide-react";
import { useConfirm } from "@/contexts/confirm-context";

interface AddJobDialogProps {
  jobs: string[];
  onAddJob: (job: string) => void;
  onDeleteJob: (job: string) => void;
  customerCountByJob: Record<string, number>;
}

export function AddJobDialog({ jobs, onAddJob, onDeleteJob, customerCountByJob }: AddJobDialogProps) {
  const { confirm, alert } = useConfirm();
  const [open, setOpen] = useState(false);
  const [newJob, setNewJob] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [deletingJob, setDeletingJob] = useState<string | null>(null);

  const handleAddJob = async () => {
    if (newJob.trim() && !jobs.includes(newJob.trim())) {
      setIsAdding(true);
      try {
        await onAddJob(newJob.trim());
        setNewJob("");
      } finally {
        setIsAdding(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddJob();
    }
  };

  const handleDeleteJob = async (job: string) => {
    const count = customerCountByJob[job] || 0;
    if (count > 0) {
      await alert({
        title: "ناتوانرێت بسڕێتەوە",
        description: `ناتوانرێت ئەم پیشەیە بسڕێتەوە چونکە ${count} کڕیار لەسەری هەیە`,
        variant: "warning"
      });
      return;
    }
    await confirm({
      title: "سڕینەوەی پیشە",
      description: `دڵنیایت لە سڕینەوەی پیشەی "${job}"؟`,
      variant: "destructive",
      confirmText: "بەڵێ، بیسڕەوە",
      cancelText: "نەخێر",
      onConfirm: async () => {
        await onDeleteJob(job);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2">
          <Briefcase className="h-3 w-3 md:h-4 md:w-4" />
          <span className="hidden sm:inline">زیادکردنی پیشە</span>
          <span className="sm:hidden">پیشە</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl h-[600px] flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>بەڕێوەبردنی پیشەکان</DialogTitle>
          <DialogDescription>{jobs.length} پیشە تۆمارکراوە</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col flex-1 overflow-hidden space-y-4">
          <div className="flex gap-2">
            <Input
              value={newJob}
              onChange={(e) => setNewJob(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ناوی پیشەی نوێ"
              className="flex-1"
            />
            <Button onClick={handleAddJob} size="icon" disabled={isAdding || !newJob.trim()}>
              {isAdding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            {jobs.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed rounded-xl">
                <Briefcase className="h-12 w-12 mb-2 opacity-20" />
                <p>هیچ پیشەیەک نییە</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {jobs.map((job) => {
                  const count = customerCountByJob[job] || 0;
                  return (
                    <div key={job} className="relative group flex items-center justify-between p-3 rounded-xl border bg-card hover:bg-accent/50 hover:shadow-sm transition-all duration-200">
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="font-medium truncate" title={job}>{job}</span>
                        <Badge variant="secondary" className="w-fit text-xs px-1.5 h-5 font-normal">
                          {count} کڕیار
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteJob(job)}
                        disabled={count > 0 || deletingJob === job}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                      >
                        {deletingJob === job ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
